import React, { useCallback, useEffect, useMemo, useState } from "react";
import { adminApi } from "@/api/adminOneClient";

/**
 * CareLogix™ ORACLE — AdminOne Grants/Funding Inbox (Phase 37)
 *
 * CEO-only inbox surfacing live Grants, Funding, Gifts, and Private Funds.
 * Auto-ingests every 6h via backend cron; supports manual Refresh.
 *
 * Tabs: All | Grants | Funding | Gifts | Private Funds | Unread
 */

const CATEGORY_LABELS = {
  all: "All",
  unread: "Unread",
  grant: "Grants",
  funding: "Funding",
  gift: "Gifts",
  private_fund: "Private Funds",
};
const CATEGORY_ORDER = ["all", "unread", "grant", "funding", "gift", "private_fund"];

const CATEGORY_BADGE = {
  grant: { label: "GRANT", color: "#facc15" },
  funding: { label: "FUNDING", color: "#38bdf8" },
  gift: { label: "GIFT", color: "#f472b6" },
  private_fund: { label: "PRIVATE FUND", color: "#a78bfa" },
};

function PriorityRing({ score }) {
  const safe = Math.max(0, Math.min(100, Number(score) || 0));
  const color = safe >= 80 ? "#22c55e" : safe >= 60 ? "#facc15" : "#94a3b8";
  return (
    <span
      data-testid="inbox-priority-ring"
      style={{
        display: "inline-flex",
        width: 38,
        height: 38,
        borderRadius: "50%",
        border: `2px solid ${color}`,
        color,
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12,
        fontWeight: 700,
        fontFamily: "JetBrains Mono, ui-monospace, monospace",
      }}
    >
      {safe}
    </span>
  );
}

function fmtUsd(range) {
  if (!Array.isArray(range) || range.length < 2) return "—";
  const [lo, hi] = range;
  if (!lo && !hi) return "—";
  const f = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
  return `$${f(lo)} – $${f(hi)}`;
}

function fmtDeadline(iso, days) {
  if (!iso) return "—";
  const d = new Date(iso);
  const date = d.toISOString().split("T")[0];
  if (typeof days === "number") return `${date} · ${days}d`;
  return date;
}

export default function AdminOneInboxPage() {
  const [tab, setTab] = useState("all");
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (selectedTab) => {
    setLoading(true);
    setError("");
    const params = {};
    if (selectedTab === "unread") params.unread_only = true;
    else if (selectedTab !== "all") params.category = selectedTab;
    try {
      const [{ data: list }, { data: sum }] = await Promise.all([
        adminApi.inboxList(params),
        adminApi.inboxSummary(),
      ]);
      setItems(list?.items || []);
      setSummary(sum || null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "load_failed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await adminApi.inboxRefresh();
      await load(tab);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "refresh_failed");
    } finally {
      setRefreshing(false);
    }
  }, [load, tab]);

  const onMarkRead = useCallback(async (notifId) => {
    setItems((prev) => prev.map((it) => it.notif_id === notifId ? { ...it, unread: false } : it));
    try { await adminApi.inboxMarkRead(notifId); } catch { /* revert optional */ }
    load(tab);
  }, [load, tab]);

  const onDismiss = useCallback(async (notifId) => {
    setItems((prev) => prev.filter((it) => it.notif_id !== notifId));
    try { await adminApi.inboxDismiss(notifId); } catch { /* ignore */ }
    load(tab);
  }, [load, tab]);

  const tabCount = useCallback((t) => {
    if (!summary) return null;
    if (t === "all") return summary.total_active ?? null;
    if (t === "unread") return summary.unread_count ?? null;
    return summary.by_category?.[t] ?? null;
  }, [summary]);

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      if (a.unread !== b.unread) return a.unread ? -1 : 1;
      return (b.priority_score || 0) - (a.priority_score || 0);
    });
  }, [items]);

  return (
    <div data-testid="adminone-inbox-page" style={{ color: "#e5e7eb", padding: "20px 24px", maxWidth: 1280, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: "'Cinzel', serif", fontSize: 28, margin: 0, color: "#facc15" }}>
            AdminOne Inbox
          </h1>
          <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4, letterSpacing: 1, textTransform: "uppercase" }}>
            Live Grants · Funding · Gifts · Private Funds
            {summary?.last_checked_at && (
              <span style={{ marginLeft: 12, color: "#64748b" }}>
                · last sync {new Date(summary.last_checked_at).toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <span data-testid="inbox-data-mode-badge" style={{
            fontSize: 11, padding: "4px 10px", borderRadius: 999,
            background: "rgba(34,197,94,0.12)", color: "#22c55e", border: "1px solid rgba(34,197,94,0.3)",
            letterSpacing: 1, textTransform: "uppercase", fontFamily: "JetBrains Mono, monospace",
          }}>
            ● VERIFIED LIVE
          </span>
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            data-testid="inbox-refresh-btn"
            style={{
              background: "#facc15", color: "#0f172a", border: "none", padding: "8px 16px",
              borderRadius: 6, fontWeight: 700, cursor: refreshing ? "wait" : "pointer", fontSize: 13,
            }}
          >
            {refreshing ? "Syncing…" : "Sync Now"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, overflowX: "auto", paddingBottom: 4 }}>
        {CATEGORY_ORDER.map((t) => {
          const active = tab === t;
          const cnt = tabCount(t);
          return (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              data-testid={`inbox-tab-${t}`}
              style={{
                background: active ? "rgba(250,204,21,0.16)" : "transparent",
                color: active ? "#facc15" : "#cbd5e1",
                border: `1px solid ${active ? "rgba(250,204,21,0.5)" : "rgba(148,163,184,0.2)"}`,
                padding: "6px 14px", borderRadius: 999, fontSize: 12, fontWeight: 600,
                cursor: "pointer", whiteSpace: "nowrap",
              }}
            >
              {CATEGORY_LABELS[t]}
              {cnt !== null && cnt !== undefined && (
                <span style={{ marginLeft: 8, opacity: 0.7 }}>· {cnt}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Error/Loading */}
      {error && <div role="alert" style={{ color: "#fca5a5", padding: "10px 14px", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 6, marginBottom: 14 }}>{error}</div>}
      {loading && <div data-testid="inbox-loading" style={{ color: "#64748b", padding: 16 }}>Loading inbox…</div>}

      {/* Items */}
      {!loading && sortedItems.length === 0 && (
        <div data-testid="inbox-empty" style={{ textAlign: "center", padding: "40px 20px", color: "#64748b", border: "1px dashed rgba(148,163,184,0.2)", borderRadius: 8 }}>
          No opportunities in this view. Try Refresh.
        </div>
      )}

      {!loading && sortedItems.length > 0 && (
        <div data-testid="inbox-items" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {sortedItems.map((it) => {
            const cb = CATEGORY_BADGE[it.category] || { label: it.category.toUpperCase(), color: "#94a3b8" };
            return (
              <div
                key={it.notif_id}
                data-testid={`inbox-item-${it.notif_id}`}
                style={{
                  background: it.unread ? "rgba(250,204,21,0.04)" : "rgba(15,23,42,0.4)",
                  border: `1px solid ${it.unread ? "rgba(250,204,21,0.25)" : "rgba(148,163,184,0.15)"}`,
                  borderLeft: `3px solid ${it.unread ? "#facc15" : "transparent"}`,
                  borderRadius: 8, padding: 14,
                  display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 14, alignItems: "start",
                }}
              >
                <PriorityRing score={it.priority_score} />

                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: 1, padding: "2px 8px",
                      borderRadius: 4, background: `${cb.color}22`, color: cb.color,
                      border: `1px solid ${cb.color}55`,
                    }}>{cb.label}</span>
                    {it.unread && <span data-testid="inbox-unread-dot" style={{ width: 8, height: 8, borderRadius: 4, background: "#facc15" }} />}
                    <span style={{ fontSize: 11, color: "#94a3b8" }}>{it.source}</span>
                    <span style={{ fontSize: 11, color: "#64748b" }}>· {it.region}</span>
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: "#f1f5f9", marginBottom: 6 }}>
                    {it.title}
                  </div>
                  {it.logix2_summary && (
                    <div style={{ fontSize: 12, color: "#cbd5e1", lineHeight: 1.55, marginBottom: 8 }}>
                      {it.logix2_summary}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 16, fontSize: 11, color: "#94a3b8", flexWrap: "wrap" }}>
                    <span><strong style={{ color: "#cbd5e1" }}>Award:</strong> {fmtUsd(it.award_range_usd)}</span>
                    <span><strong style={{ color: "#cbd5e1" }}>Deadline:</strong> {fmtDeadline(it.deadline_iso, it.days_to_deadline)}</span>
                    <span><strong style={{ color: "#cbd5e1" }}>Fit:</strong> {it.fit_score}/100</span>
                    {it.hitl_required && (
                      <span style={{ color: "#f97316" }}>● HITL required</span>
                    )}
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {it.unread && (
                    <button
                      type="button"
                      onClick={() => onMarkRead(it.notif_id)}
                      data-testid={`inbox-mark-read-${it.notif_id}`}
                      style={{
                        background: "transparent", color: "#94a3b8", border: "1px solid rgba(148,163,184,0.3)",
                        padding: "4px 10px", borderRadius: 4, fontSize: 11, cursor: "pointer",
                      }}
                    >Mark read</button>
                  )}
                  <button
                    type="button"
                    onClick={() => onDismiss(it.notif_id)}
                    data-testid={`inbox-dismiss-${it.notif_id}`}
                    style={{
                      background: "transparent", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.3)",
                      padding: "4px 10px", borderRadius: 4, fontSize: 11, cursor: "pointer",
                    }}
                  >Dismiss</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Compliance footer */}
      <div style={{ marginTop: 20, fontSize: 10, color: "#64748b", textAlign: "center", letterSpacing: 0.5 }}>
        Educational market intelligence only. Not financial advice. Human review required before action.
      </div>
    </div>
  );
}
